import { Button, Input, Tag } from 'antd';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle } from 'src/components/PageTitle';
import { ReactComponent as Close } from '../assets/img/close.svg';
import { getCommonRoutePath, getUniversityId, isGlobalVersionTwo, isUniGroup } from '../helpers/Helpers';
import { getNextItems } from '../helpers/getNextItems';
import isEqualPaths from '../helpers/isEqualPaths';
import { sessionStorageWrapper } from '../helpers/storageFactory';
import { clearFilterTerms, removeFilterTerm } from '../redux/actions/filtersActions';
import { resetMainInfo } from '../redux/actions/mainActions';
import { fetchData, resetInfo } from '../redux/actions/randomItems';
import { clearSearch, removeSearchTerm, setSearchTerm } from '../redux/actions/searchActions';
import { setUniGroup } from '../redux/actions/widgetActions';

const DropdownFilter = React.lazy(() => import('./DropdownFilter'));
const DropdownFilterMobile = React.lazy(() => import('./DropdownFilterMobile'));
const NoSearchResultPlaceholder = React.lazy(() => import(/* match-me-popup */ './NoSearchResultPlaceholder'));
const TapPageLogo = React.lazy(() => import('../components/account/TapPageLogo'));

import '../assets/styles/custom/components/header.scss';

const TapPageHead = ({
    onProfilePage,
    searchGroupChats,
    searchUsers,
    searchPosts,
    searchFaqs,
    searchLiveStreams,
    showEmptyPlaceholder,
}) => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const { showGroupChats, showUsers, showFaqs, showPosts } = useSelector((state) => state.main);
    const account = useSelector((state) => state.account);

    const {
        isMobile,
        groupUniversityId,
        universityIds,
        titles: { page_title, page_desc, display_search_bar, display_tap_logo },
        filters,
        university_name: universityName,
        preFilterItems,
        institution,
    } = useSelector((state) => state.widget);
    const searchTerms = useSelector((state) => state.searchTerms);
    const filterTerms = useSelector((state) => state.filterTerms);
    const [searchTerm, setSearchTermState] = useState('');
    const [showFiltersForMobile, setShowFiltersForMobile] = useState(false);

    useEffect(() => {
        if (isGlobalVersionTwo && isMobile) {
            setShowFiltersForMobile(true);
        }
    }, [isMobile]);

    const handleChange = useCallback(({ target }) => {
        setSearchTermState(target.value);
    }, []);

    const handleSubmit = useCallback(
        (e, button) => {
            try {
                const key = e.keyCode;

                if (button || (key && key === 13)) {
                    if (searchTerm.trim()) {
                        requestSearch(searchTerm);
                        dispatch(setSearchTerm(searchTerm));
                        setSearchTermState('');
                    }
                }
            } catch (e) {
                console.log(e);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchTerm, dispatch] // TODO: Bug: dependency array is not exhaustive
    );

    const requestSearch = useCallback(
        (
            value = null,
            trackBy = 'search',
            dropType = null,
            dropValues = null,
            valueToRemove = '',
            removeAll = false
        ) => {
            const location = window.location;

            const universityId = getUniversityId();
            const uniGroup = isUniGroup();

            filterTerms.fixed = removeAll ? [] : dropType === 'fixed' && dropValues ? dropValues : filterTerms.fixed;
            filterTerms.customFilters = removeAll
                ? []
                : dropType === 'custom' && dropValues
                  ? dropValues
                  : filterTerms.customFilters;
            let preparedSearchItems = removeAll ? [] : searchTerms.items;

            if (valueToRemove.length > 0) {
                filterTerms.fixed = filterTerms.fixed.filter(
                    (term) => term.name.toLowerCase() !== valueToRemove.toLowerCase()
                );
                filterTerms.customFilters = filterTerms.customFilters.filter(
                    (term) => term.toLowerCase() !== valueToRemove.toLowerCase()
                );
                preparedSearchItems = preparedSearchItems.filter(
                    (term) => term.toLowerCase() !== valueToRemove.toLowerCase()
                );
            }
            const fixedFilterTerms = filterTerms.fixed;
            const customFilterTerms = filterTerms.customFilters;

            const filteredUniversityIds = [];
            const groupCountryIds = [];
            const preparedFilters = [];
            const preparedFiltersIds = [];
            const countryIds = [];
            const profileTypeIds = [];
            const subjectIds = [];

            if (groupUniversityId && fixedFilterTerms && fixedFilterTerms.length) {
                for (const filterTerm of fixedFilterTerms) {
                    // If group_universities we are filtering by sending universitiesIds and without sending it as filters
                    if (filterTerm.key === 'group_universities') {
                        if (filterTerm.partnerUniIds) {
                            for (const id of filterTerm.partnerUniIds) {
                                filteredUniversityIds.push(id);
                            }
                        } else {
                            filteredUniversityIds.push(filterTerm.id);
                        }
                    } else if (filterTerm.key === 'university_countries') {
                        groupCountryIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_countries') {
                        countryIds.push(filterTerm.id);
                    } else if (filterTerm.key === 'account_type') {
                        profileTypeIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_subject') {
                        subjectIds.push(filterTerm.id);
                    } else {
                        preparedFilters.push(filterTerm);
                    }
                }
                dispatch(setUniGroup(filteredUniversityIds));
                // we must reset info as onlyOneScreen when we switched university
                dispatch(resetInfo());
            } else if (groupUniversityId && universityIds.length) {
                for (const filterTerm of fixedFilterTerms) {
                    // If university_countries we are filtering by sending groupCountryIds and without sending it as filters
                    if (filterTerm.key === 'university_countries') {
                        groupCountryIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_countries') {
                        countryIds.push(filterTerm.id);
                    } else if (filterTerm.key === 'account_type') {
                        profileTypeIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_subject') {
                        subjectIds.push(filterTerm.id);
                    } else {
                        preparedFilters.push(filterTerm);
                    }
                }
                dispatch(setUniGroup([]));
                dispatch(resetMainInfo());
                dispatch(resetInfo());
            } else {
                for (const filterTerm of fixedFilterTerms) {
                    // If university_countries we are filtering by sending groupCountryIds and without sending it as filters
                    if (filterTerm.key === 'university_countries') {
                        groupCountryIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_countries') {
                        countryIds.push(filterTerm.id);
                    } else if (filterTerm.key === 'account_type') {
                        profileTypeIds.push(filterTerm.id);
                    } else if (filterTerm.key === '_subject') {
                        subjectIds.push(filterTerm.id);
                    } else {
                        preparedFilters.push(filterTerm);
                    }
                }
            }

            for (const item of preparedFilters) {
                preparedFiltersIds.push(item.id);
            }

            let foundItem = false;
            let foundCustomItem = false;
            let newTerms = [];
            if (preparedSearchItems && customFilterTerms) {
                newTerms = preparedSearchItems.concat(
                    customFilterTerms.filter(
                        (item) => !preparedSearchItems.find((term) => term.toLowerCase() === item.toLowerCase())
                    )
                );
            } else if (preparedSearchItems) {
                newTerms = preparedSearchItems;
            } else if (customFilterTerms) {
                newTerms = customFilterTerms;
            }

            if (value) {
                foundItem = preparedSearchItems.find((term) => term.toLowerCase() === value.toLowerCase());
                foundCustomItem = customFilterTerms.find((term) => term.toLowerCase() === value.toLowerCase());
                if (!foundItem && !foundCustomItem) newTerms.push(value);
                let trackName = onProfilePage ? 'Used Search on the Profile page' : 'Used Search on the main page';
                let searchValues = value;
                if (trackBy === 'dropdown') {
                    trackName = onProfilePage ? 'Used Dropdown on the Profile page' : 'Used Dropdowns on the main page';
                    searchValues = value;
                } else {
                    if (preparedSearchItems.length || customFilterTerms.length) {
                        searchValues = [...preparedSearchItems, ...customFilterTerms];
                        if (!foundItem && !foundCustomItem) searchValues.push(value);
                    }
                }
                sessionStorageWrapper.setItem(
                    'searchData',
                    JSON.stringify({ trackName: trackName, value: searchValues })
                );
            } else if (trackBy === 'dropdown') {
                const trackName = onProfilePage
                    ? 'Used Dropdown on the Profile page'
                    : 'Used Dropdowns on the main page';
                sessionStorageWrapper.setItem(
                    'searchData',
                    JSON.stringify({ trackName: trackName, value: fixedFilterTerms, fixedDrop: true })
                );
            }

            if (trackBy === 'dropdown') {
                const currentUsedDropNumber = fixedFilterTerms.length + customFilterTerms.length + (value ? 1 : 0);
                if (currentUsedDropNumber > 1) {
                    const trackName =
                        currentUsedDropNumber === 2
                            ? 'Using 2 Dropdowns at the same time'
                            : 'Using 3 Dropdowns at the same time';
                    sessionStorageWrapper.setItem(
                        'searchData',
                        JSON.stringify({
                            trackName: trackName,
                            value: value || (fixedFilterTerms ? fixedFilterTerms[fixedFilterTerms.length - 1] : null),
                            fixedDrop: !value,
                        })
                    );
                }
            }

            if (
                (!uniGroup && isEqualPaths(location.pathname, '/' + params.university_id)) ||
                (uniGroup &&
                    (isEqualPaths(location.pathname, '/group/' + params.university_id) ||
                        window.globalUniversityId ||
                        window.globalGroupId) &&
                    !isGlobalVersionTwo)
            ) {
                let options = {};
                if (preparedSearchItems.length || value || customFilterTerms.length) {
                    options = {
                        search: newTerms.filter((term) => term),
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: false,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                } else {
                    options = {
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: !preparedFiltersIds.length,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                }
                dispatch(fetchData(options));

                if (window.globalUniversityId || window.globalGroupId) {
                    navigate({
                        pathname: '/',
                        resetContent: true,
                    });
                }
            } else if (isUniGroup() && !isGlobalVersionTwo) {
                let options = {};
                if (preparedSearchItems.length || value || customFilterTerms.length) {
                    options = {
                        search: newTerms.filter((term) => term),
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: false,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                } else {
                    options = {
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: !preparedFiltersIds.length,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                }
                navigate({
                    pathname: getCommonRoutePath(),
                });
                setTimeout(() => {
                    dispatch(fetchData(options));
                }, 1);
            } else {
                let options = {};
                if (preparedSearchItems.length || value || customFilterTerms.length) {
                    options = {
                        search: newTerms.filter((term) => term),
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: false,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                } else {
                    options = {
                        filterTerms: preparedFiltersIds,
                        groupCountryIds,
                        random: !preparedFiltersIds.length,
                        countryIds,
                        profileTypeIds,
                        subjectIds,
                    };
                }

                if (searchGroupChats) {
                    searchGroupChats(options);
                } else if (searchUsers) {
                    searchUsers(
                        options,
                        isGlobalVersionTwo
                            ? () =>
                                  getNextItems(
                                      preparedSearchItems,
                                      filterTerms,
                                      setSearchTerm,
                                      uniGroup ? null : universityId
                                  )
                            : null
                    );
                } else if (searchPosts) {
                    searchPosts(options);
                } else if (searchFaqs) {
                    searchFaqs(options);
                } else if (searchLiveStreams) {
                    searchLiveStreams(options);
                } else if (fetchData) {
                    dispatch(fetchData(options));
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            dispatch,
            searchTerms,
            filterTerms,
            params,
            onProfilePage,
            navigate,
            searchGroupChats,
            searchUsers,
            searchPosts,
            searchFaqs,
            searchLiveStreams,
        ] // TODO: Bug: dependency array is not exhaustive
    );

    const handleDeleteTerm = (term) => {
        dispatch(removeSearchTerm(term));
        dispatch(removeFilterTerm(term));
        requestSearch(null, 'search', null, null, term);
    };

    const handleClearTerms = useCallback(() => {
        dispatch(clearSearch());
        dispatch(clearFilterTerms());
        requestSearch(null, 'search', null, null, '', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]); // TODO: Bug: dependency array is not exhaustive

    const handleFilterDisplay = useCallback(() => {
        setShowFiltersForMobile(!showFiltersForMobile);
    }, [showFiltersForMobile]);

    const filtersView = useMemo(() => {
        return filters && filters.length > 0 ? (
            <Button
                className={`filter-button ${showFiltersForMobile ? 'show-filters' : ''}`}
                onClick={handleFilterDisplay}
                aria-label="Mobile Filter"
            >
                <svg className="filter-icon" viewBox="-2.5 -2.5 24 24" width="24" height="24">
                    <path
                        d="M1.895 1.815l5.881 7.35a1.815 1.815 0 0 1 .4 1.134v6.033l1.812-1.362V10.3a1.815 1.815 0 0 1 .4-1.133l5.881-7.35H1.9zm0-1.815h14.371a1.815 1.815 0 0 1 1.416 2.949L11.8 10.3v4.67a1.815 1.815 0 0 1-.726 1.452l-1.812 1.361a1.815 1.815 0 0 1-2.9-1.452V10.3L.478 2.949A1.815 1.815 0 0 1 1.895 0z"
                        transform="translate(-.08)"
                    />
                </svg>
            </Button>
        ) : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, showFiltersForMobile]); // TODO: Bug: dependency array is not exhaustive

    return (
        <div className="widget-head">
            <div className="head-container">
                {!isGlobalVersionTwo && !account.isAuthFromMobApp && (
                    <div>
                        <div className={'page-title-container' + (display_tap_logo ? ' visible-logo' : '')}>
                            <PageTitle
                                pageTitle={page_title}
                                universityName={universityName}
                                universityId={institution?.id}
                            />
                            {display_tap_logo && <TapPageLogo />}
                        </div>
                        <p className="page-description">
                            {page_desc ||
                                `Do you want to speak to someone about their experiences at ${universityName}? Start a chat with our friendly ambassadors now!`}
                        </p>
                    </div>
                )}
                {display_search_bar && (
                    <>
                        {isGlobalVersionTwo ? (
                            <>
                                {!isMobile && (
                                    <div className="search-form">
                                        <div className="search-form-input-v2">
                                            <div className="search-input-block">
                                                <p>Find ambassadors by location, subject, university and more…</p>
                                                <Input
                                                    type="text"
                                                    aria-label="Search for an ambassador or content"
                                                    aria-required="false"
                                                    value={searchTerm}
                                                    onChange={handleChange}
                                                    onKeyUp={handleSubmit}
                                                    placeholder="..."
                                                />
                                            </div>
                                            <Button
                                                className="widget-search-tab-btn-v2"
                                                onClick={(e) => handleSubmit(e, 'button')}
                                                aria-label="Search"
                                            >
                                                <span>Search</span>
                                                <svg
                                                    className={`search-icon-v2 ${
                                                        isMobile ? 'tap-mobile' : ''
                                                    } jam jam-search`}
                                                    viewBox="-2.5 -2.5 20 22"
                                                    width="20"
                                                    height="18"
                                                    preserveAspectRatio="xMinYMin"
                                                    onClick={(e) => handleSubmit(e, 'button')}
                                                >
                                                    <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zm6.32-1.094l3.58 3.58a1 1 0 1 1-1.415 1.413l-3.58-3.58a8 8 0 1 1 1.414-1.414z" />
                                                </svg>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {isMobile && showEmptyPlaceholder && (
                                    <NoSearchResultPlaceholder mobilePlaceholder={true} />
                                )}
                                {isMobile && (!!searchTerms.bubbles.length || searchTerms.groupUniversityBubble) && (
                                    <>
                                        {!isGlobalVersionTwo && (
                                            <Tag
                                                closable
                                                onClose={handleClearTerms}
                                                onClick={handleClearTerms}
                                                className="widget-footer-clear-btn"
                                            >
                                                Clear All Search filters
                                            </Tag>
                                        )}
                                    </>
                                )}
                                <div className="search-form">
                                    <div className="search-form-input">
                                        <svg
                                            className={`search-icon ${isMobile ? 'tap-mobile' : ''} jam jam-search`}
                                            viewBox="-2.5 -2.5 24 24"
                                            width="24"
                                            height="24"
                                            preserveAspectRatio="xMinYMin"
                                            onClick={(e) => handleSubmit(e, 'button')}
                                        >
                                            <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12zm6.32-1.094l3.58 3.58a1 1 0 1 1-1.415 1.413l-3.58-3.58a8 8 0 1 1 1.414-1.414z" />
                                        </svg>
                                        <Input
                                            type="text"
                                            className={`search-input ${isMobile ? 'tap-mobile' : ''}`}
                                            aria-label="Search for an ambassador or content"
                                            aria-required="false"
                                            value={searchTerm}
                                            onChange={handleChange}
                                            onKeyUp={handleSubmit}
                                        />
                                    </div>

                                    {isMobile ? (
                                        filtersView
                                    ) : (
                                        <Button
                                            className="widget-tab-btn widget-head-filter-btn"
                                            onClick={(e) => handleSubmit(e, 'button')}
                                            aria-label="Search"
                                        >
                                            Search
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
                {!isMobile ? (
                    <ul className="dropdowns-row">
                        {filters &&
                            !filterTerms.matchMe &&
                            filters.map((filter) => (
                                <Suspense fallback={null} key={filter.id}>
                                    <DropdownFilter
                                        preFilterItems={preFilterItems}
                                        requestSearch={requestSearch}
                                        searchTerms={searchTerms.bubbles}
                                        filterTerms={filterTerms}
                                        {...filter}
                                    />
                                </Suspense>
                            ))}
                    </ul>
                ) : !display_search_bar && !isGlobalVersionTwo ? (
                    filtersView
                ) : null}

                <div className="widget-head-bottom">
                    {showGroupChats}
                    {showUsers}
                    {showFaqs}
                    {showPosts}
                </div>
                {(!!searchTerms.bubbles.length || searchTerms.groupUniversityBubble) && (
                    <>
                        {isGlobalVersionTwo && isMobile ? (
                            <></>
                        ) : (
                            <div className="search-footer">
                                <div className="search-term-wrapper">
                                    {searchTerms.groupUniversityBubble && (
                                        <span key={searchTerms.groupUniversityBubble} className="search-term">
                                            {searchTerms.groupUniversityBubble}
                                            <Button
                                                aria-label={`Delete search term ${searchTerms.groupUniversityBubble}`}
                                                onClick={() => handleDeleteTerm(searchTerms.groupUniversityBubble)}
                                                className="search-term__close-icon"
                                            >
                                                <Close />
                                            </Button>
                                        </span>
                                    )}
                                    {searchTerms.bubbles.map((term) => {
                                        return (
                                            <span key={term} className="search-term" content={term}>
                                                {term}
                                                <Button
                                                    aria-label={`Delete search term ${term}`}
                                                    onClick={() => handleDeleteTerm(term)}
                                                    className="search-term__close-icon"
                                                >
                                                    <Close data-automation-id={`delete-search-term-${term}`} />
                                                </Button>
                                            </span>
                                        );
                                    })}
                                </div>
                                {!isMobile && (!!searchTerms.bubbles.length || searchTerms.groupUniversityBubble) && (
                                    <Tag
                                        closable
                                        onClose={handleClearTerms}
                                        onClick={handleClearTerms}
                                        className="widget-footer-clear-btn"
                                    >
                                        Clear All Search filters
                                    </Tag>
                                )}
                            </div>
                        )}
                    </>
                )}
                {isMobile ? (
                    <ul
                        className={
                            (showFiltersForMobile ? 'chats-row tap-mobile' : 'chats-row tap-mobile hidden-block') +
                            (isGlobalVersionTwo ? ' isTHEFilters' : '')
                        }
                    >
                        {filters &&
                            filters
                                .filter(() => {
                                    if (!isGlobalVersionTwo) return true;

                                    return true;
                                })
                                .map((filter) => (
                                    <Suspense fallback={null} key={filter.id}>
                                        <DropdownFilterMobile
                                            preFilterItems={preFilterItems}
                                            searchTerms={searchTerms.bubbles}
                                            filterTerms={filterTerms}
                                            requestSearch={requestSearch}
                                            isTHEFilters={isGlobalVersionTwo}
                                            {...filter}
                                        />
                                    </Suspense>
                                ))}
                    </ul>
                ) : null}
            </div>
        </div>
    );
};

export default TapPageHead;
